import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getSearchSuggestions } from '../../services'
import { SearchSuggestion } from '../../types'
import { SEARCH_RESULT_TYPES } from '../../constants'
import PeopleIcons from '@/icons/PeopleIcons'
import ServiceIcons from '@/icons/ServiceIcons'
import InvoiceIcons from '@/icons/InvoiceIcons'
import SearchIcons from '@/icons/SearchIcons'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface SearchSuggestionsProps {
    query: string
    onSuggestionClick: (suggestion: string) => void
    className?: string
}

const SUGGESTION_ICONS = {
    customer: <PeopleIcons className="w-4 h-4 text-blue-500" />,
    service: <ServiceIcons className="w-4 h-4 text-green-500" />,
    contract: <InvoiceIcons className="w-4 h-4 text-orange-500" />,
    invoice: <InvoiceIcons className="w-4 h-4 text-purple-500" />,
}

const SearchSuggestions: React.FC<SearchSuggestionsProps> = ({
    query,
    onSuggestionClick,
    className = '',
}) => {
    const { t } = useTranslation('search')

    const {
        data: suggestions = [],
        isLoading,
        error,
    } = useQueryWithHandler<SearchSuggestion[]>(
        {
            queryKey: [QUERY_KEYS.SEARCH_SUGGESTIONS, query],
            queryFn: () => getSearchSuggestions(query),
            enabled: query.length >= 2,
            staleTime: 1000 * 60 * 5, // 5 minutes
            retry: 1, // Only retry once
        },
        { showLoading: false, showError: false }
    )

    const groupedSuggestions = useMemo(() => {
        return suggestions.reduce(
            (
                acc: Record<string, SearchSuggestion[]>,
                suggestion: SearchSuggestion
            ) => {
                if (!acc[suggestion.type]) {
                    acc[suggestion.type] = []
                }
                acc[suggestion.type].push(suggestion)
                return acc
            },
            {}
        )
    }, [suggestions])

    if (query.length < 2) return null

    if (isLoading) {
        return (
            <div className={`p-4 ${className}`}>
                <div className="flex items-center gap-2 text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                    <Typogaphy className="text-sm">
                        {t('loading-suggestions')}...
                    </Typogaphy>
                </div>
            </div>
        )
    }

    // Show nothing if error or no suggestions
    if (error || suggestions.length === 0) return null

    return (
        <div className={`${className}`}>
            <div className="p-3 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-2">
                    <SearchIcons className="w-4 h-4 text-gray-500" />
                    <Typogaphy className="text-sm font-medium text-gray-700">
                        {t('suggestions')} ({suggestions.length})
                    </Typogaphy>
                </div>
            </div>

            <div className="max-h-60 overflow-y-auto">
                {Object.entries(groupedSuggestions).map(
                    ([type, typeSuggestions]: [string, SearchSuggestion[]]) => {
                        const typeConfig =
                            SEARCH_RESULT_TYPES[
                                type as keyof typeof SEARCH_RESULT_TYPES
                            ]

                        return (
                            <div key={type}>
                                {/* Type Header */}
                                <div className="px-3 py-2 bg-gray-25 border-b border-gray-100">
                                    <div className="flex items-center gap-2">
                                        {
                                            SUGGESTION_ICONS[
                                                type as keyof typeof SUGGESTION_ICONS
                                            ]
                                        }
                                        <Typogaphy className="text-xs font-medium text-gray-600">
                                            {t(typeConfig?.label)} (
                                            {typeSuggestions.length})
                                        </Typogaphy>
                                    </div>
                                </div>

                                {/* Suggestions List */}
                                <div>
                                    {typeSuggestions.map(
                                        (
                                            suggestion: SearchSuggestion,
                                            index: number
                                        ) => (
                                            <button
                                                key={`${type}-${index}`}
                                                onClick={() =>
                                                    onSuggestionClick(
                                                        suggestion.text
                                                    )
                                                }
                                                className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                                            >
                                                <div className="flex items-center justify-between">
                                                    <Typogaphy className="text-sm text-gray-800">
                                                        {suggestion.text}
                                                    </Typogaphy>
                                                    <span className="text-xs text-gray-400">
                                                        {suggestion.count}{' '}
                                                        {t('results')}
                                                    </span>
                                                </div>
                                            </button>
                                        )
                                    )}
                                </div>
                            </div>
                        )
                    }
                )}
            </div>
        </div>
    )
}

export default SearchSuggestions
